import { EASTER_EGGS } from "@/shared/constants";

// Detection is intentionally inert for real stations: it runs only when no
// stationId is supplied (unscoped/test contexts). There is no per-station
// disable list — the presence of a stationId alone turns the feature off.
const FEATURE_DISABLED = (stationId?: string | null): boolean =>
  !!stationId;

/**
 * Returns the first easter-egg phrase still present in `text`, or null.
 * Treats the whole text as case-insensitive. The CO Review approve flow uses this
 * to gate Approve when the officer hasn't removed the inserted phrase yet.
 *
 * Gated by FEATURE_DISABLED: detection only runs when no stationId is provided,
 * so every station-scoped report is exempt.
 */
export function findEasterEggPhrase(
  text: string | null | undefined,
  stationId?: string | null,
): string | null {
  if (!text) return null;
  if (FEATURE_DISABLED(stationId)) return null;
  const haystack = text.toLowerCase();
  for (const phrase of EASTER_EGGS) {
    if (haystack.includes(phrase.toLowerCase())) {
      return phrase;
    }
  }
  return null;
}

export function containsAnyEasterEgg(
  text: string | null | undefined,
  stationId?: string | null,
): boolean {
  return findEasterEggPhrase(text, stationId) !== null;
}

/**
 * Walk every narrative entry in a report's formData and return the first egg found,
 * or null if all narratives are clean.
 */
export function findEasterEggInNarratives(
  formData: Record<string, unknown> | null | undefined,
  stationId?: string | null,
): { narrativeIndex: number; phrase: string } | null {
  if (!formData) return null;
  const narratives = formData["Narratives"];
  if (!Array.isArray(narratives)) return null;
  for (let i = 0; i < narratives.length; i++) {
    const entry = narratives[i] as Record<string, unknown> | null;
    if (!entry) continue;
    // eForce uses `Text`; generic uses `Narrative`. Try both.
    const candidate =
      typeof entry.Text === "string"
        ? entry.Text
        : typeof entry.Narrative === "string"
          ? entry.Narrative
          : null;
    const phrase = findEasterEggPhrase(candidate, stationId);
    if (phrase) {
      return { narrativeIndex: i, phrase };
    }
  }
  return null;
}
